import React, { useState, useEffect } from 'react';
import {
    Modal, Container, Row, Col, Card,
    Form, Button, Spinner, Badge
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getSignedImageUrl } from './useTransactionDetails';

export const MATERIAL_OPTIONS = [
    "10KT Gold Yellow",
    "14KT Gold Yellow",
    "18KT Gold Yellow",
    "24KT Gold Yellow",
    "10KT Gold White",
    "14KT Gold White",
    "18KT Gold White",
    "24KT Gold White",
    "Silver",
];

/**
 * LineItemModal
 *
 * Props:
 *   show              {boolean}
 *   lineItem          {object|null}  – the line item being edited (shallow copy)
 *   type              {string}       – ticket type (e.g. 'repair', 'sale')
 *   transactionId     {string}
 *   onClose           {() => void}
 *   onSave            {(item) => Promise<void>}
 *   onDelete          {(item) => Promise<void>}
 *   onUploadImage     {(file) => Promise<{ key: string, imageUrl: string }>}
 *   onDeleteImage     {(key: string) => Promise<void>}
 *   readOnly          {boolean}      – disables all edits; hides Save/Delete/image buttons
 *   materialOptions   {string[]}     – override MATERIAL_OPTIONS if needed
 */
export function LineItemModal({
    show,
    lineItem,
    type,
    transactionId,
    onClose,
    onSave,
    onDelete,
    onUploadImage,
    onDeleteImage,
    readOnly = false,
    materialOptions = MATERIAL_OPTIONS,
}) {
    const { t } = useTranslation();

    const [editing, setEditing] = useState(null);
    const [signedImageUrl, setSignedImageUrl] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [isDeletingImage, setIsDeletingImage] = useState(false);
    // Key of an image uploaded this session but not yet committed via Save
    const [pendingKey, setPendingKey] = useState(null);

    // Sync external lineItem → local editing copy
    useEffect(() => {
        setEditing(lineItem ? { ...lineItem } : null);
        setSignedImageUrl(null);
        setPendingKey(null);
    }, [lineItem]);

    // Fetch a fresh signed URL whenever the modal opens or imageKey changes
    useEffect(() => {
        if (!show || !editing?.imageKey) return;

        let isMounted = true;
        getSignedImageUrl(editing.imageKey, type, transactionId).then((url) => {
            if (isMounted) setSignedImageUrl(url);
        });
        return () => { isMounted = false; };
    }, [show, editing?.imageKey, type, transactionId]);

    // ── Field helpers ─────────────────────────────────────────────────────────
    const setField = (field, value) =>
        setEditing((prev) => ({ ...prev, [field]: value }));

    const setNumber = (field, value) =>
        setEditing((prev) => {
            const next = value === '' ? '' : Number(value);
            const updated = { ...prev, [field]: next };
            if (field === 'discountAmount') {
                updated.discountApplied = value !== '' && Number(value) > 0;
            }
            return updated;
        });

    const setCheck = (field, checked) =>
        setEditing((prev) => ({ ...prev, [field]: checked }));

    // ── Image actions ─────────────────────────────────────────────────────────
    const handleImageUpload = async (file) => {
        if (!file || !onUploadImage) return;
        try {
            setIsUploadingImage(true);
            // If there's already a pending (unsaved) upload, delete it before replacing
            if (pendingKey && onDeleteImage) {
                try { await onDeleteImage(pendingKey); } catch (e) { console.error('Error cleaning up previous pending image:', e); }
            }
            const { key, imageUrl } = await onUploadImage(file);
            setPendingKey(key);
            setEditing((prev) => ({
                ...prev,
                imageKey: key,
                imageURL: imageUrl,
                thumbnailURL: imageUrl,
            }));
        } catch (error) {
            console.error('Error uploading image:', error);
        } finally {
            setIsUploadingImage(false);
        }
    };

    const handleImageDelete = async () => {
        if (!editing?.imageKey || !onDeleteImage) return;
        try {
            setIsDeletingImage(true);
            await onDeleteImage(editing.imageKey);
            setPendingKey(null);
            setSignedImageUrl(null);
            setEditing((prev) => ({
                ...prev,
                imageKey: '',
                imageURL: '',
                thumbnailURL: '',
            }));
        } catch (error) {
            console.error('Error deleting image:', error);
        } finally {
            setIsDeletingImage(false);
        }
    };

    // Closing without saving — delete any image uploaded this session
    const handleClose = async () => {
        if (pendingKey && onDeleteImage) {
            try { await onDeleteImage(pendingKey); } catch (e) { console.error('Error cleaning up pending image on close:', e); }
        }
        onClose();
    };

    // ── Line item actions ─────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!editing?.id) return;
        try {
            setIsSaving(true);
            setPendingKey(null); // committed — don't delete on close
            await onSave(editing);
            onClose();
        } catch (error) {
            console.error('Error saving line item:', error);
            // restore pendingKey so a subsequent close still cleans up
            setPendingKey(editing?.imageKey || null);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!editing?.id) return;
        try {
            setIsDeleting(true);
            await onDelete(editing.id);
            onClose();
        } catch (error) {
            console.error('Error deleting line item:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const busy = isSaving || isDeleting || isUploadingImage || isDeletingImage;
    const hasImage = Boolean(editing?.imageKey);
    const imageDisplay = signedImageUrl || editing?.imageURL || editing?.thumbnailURL;
    const ro = readOnly; // shorthand used throughout JSX

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered fullscreen>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    {ro ? t('View Line Item') : t('Edit Line Item')}
                    {editing?.id ? ` - ${editing.id}` : ''}
                    {ro && (
                        <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.7rem' }}>
                            {t('Read Only')}
                        </Badge>
                    )}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {!editing ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <Container fluid>
                        <Row className="mb-4">
                            {/* ── Image panel ── */}
                            <Col md={4}>
                                <Card className="h-100">
                                    <Card.Header>{t('Images')}</Card.Header>
                                    <Card.Body className="text-center d-flex flex-column align-items-center gap-3">
                                        {imageDisplay ? (
                                            <img
                                                src={imageDisplay}
                                                alt={editing.title || 'Line Item'}
                                                style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <div className="text-muted py-4">{t('No image available')}</div>
                                        )}

                                        {!ro && (
                                            <div className="d-flex gap-2 flex-wrap justify-content-center">
                                                {/* Upload / Replace */}
                                                {onUploadImage && (
                                                    <Form.Group className="mb-0">
                                                        <Form.Label className="btn btn-outline-primary mb-0">
                                                            {isUploadingImage
                                                                ? t('Uploading...')
                                                                : hasImage
                                                                    ? t('Replace Image')
                                                                    : t('Upload Image')}
                                                            <Form.Control
                                                                type="file"
                                                                accept="image/*"
                                                                style={{ display: 'none' }}
                                                                disabled={busy}
                                                                onChange={(e) => {
                                                                    const file = e.target.files?.[0];
                                                                    if (file) handleImageUpload(file);
                                                                    e.target.value = '';
                                                                }}
                                                            />
                                                        </Form.Label>
                                                    </Form.Group>
                                                )}

                                                {/* Delete image */}
                                                {onDeleteImage && hasImage && (
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        disabled={busy}
                                                        onClick={handleImageDelete}
                                                    >
                                                        {isDeletingImage ? t('Removing...') : t('Remove Image')}
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* ── Form panels ── */}
                            <Col md={8}>
                                {/* General */}
                                <Card className="mb-4">
                                    <Card.Header>{t('General Information')}</Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('ID')}</Form.Label>
                                                    <Form.Control value={editing.id || ''} disabled />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('Type')}</Form.Label>
                                                    <Form.Control value={editing.lineItemType || ''} disabled />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('Title')}</Form.Label>
                                            <Form.Control
                                                value={editing.title || ''}
                                                disabled={ro}
                                                onChange={(e) => setField('title', e.target.value)}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-0">
                                            <Form.Label>{t('Description')}</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                value={editing.description || ''}
                                                disabled={ro}
                                                onChange={(e) => setField('description', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Card.Body>
                                </Card>

                                {/* Pricing */}
                                <Card className="mb-4">
                                    <Card.Header>{t('Pricing')}</Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('Quantity')}</Form.Label>
                                                    <Form.Control
                                                        type="number" min="1" step="1"
                                                        value={editing.quantity ?? 1}
                                                        disabled={ro}
                                                        onChange={(e) => setNumber('quantity', e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('Cost')}</Form.Label>
                                                    <Form.Control
                                                        type="number" step="0.01" min="0"
                                                        value={editing.cost ?? ''}
                                                        disabled={ro}
                                                        onChange={(e) => setNumber('cost', e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('Discount Amount')}</Form.Label>
                                                    <Form.Control
                                                        type="number" step="0.01" min="0"
                                                        value={editing.discountAmount ?? ''}
                                                        disabled={ro}
                                                        onChange={(e) => setNumber('discountAmount', e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={4}>
                                                <Form.Check
                                                    type="switch"
                                                    id="edit-discount-applied-switch"
                                                    label={t('Discount Applied')}
                                                    checked={Boolean(editing.discountApplied)}
                                                    disabled={ro}
                                                    onChange={(e) => setCheck('discountApplied', e.target.checked)}
                                                />
                                            </Col>
                                            <Col md={4}>
                                                <Form.Check
                                                    type="switch"
                                                    id="edit-tax-exempt-switch"
                                                    label={t('Tax Exempt')}
                                                    checked={Boolean(editing.taxExempt)}
                                                    disabled={ro}
                                                    onChange={(e) => setCheck('taxExempt', e.target.checked)}
                                                />
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Material & Weight */}
                                <Card>
                                    <Card.Header>{t('Material & Weight')}</Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('Material')}</Form.Label>
                                                    <Form.Select
                                                        value={editing.material || ''}
                                                        disabled={ro}
                                                        onChange={(e) => setField('material', e.target.value)}
                                                    >
                                                        <option value="">{t('Select Material')}</option>
                                                        {materialOptions.map((m) => (
                                                            <option key={m} value={m}>{m}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('Weigh In')}</Form.Label>
                                                    <Form.Control
                                                        type="number" step="0.01"
                                                        value={editing.weighIn ?? ''}
                                                        disabled={ro}
                                                        onChange={(e) => setNumber('weighIn', e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('Weigh Out')}</Form.Label>
                                                    <Form.Control
                                                        type="number" step="0.01"
                                                        value={editing.weighOut ?? ''}
                                                        disabled={ro}
                                                        onChange={(e) => setNumber('weighOut', e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                )}
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between">
                {/* Delete — hidden in read-only mode */}
                {!ro ? (
                    <Button
                        variant="danger"
                        onClick={handleDelete}
                        disabled={busy || !editing}
                    >
                        {isDeleting ? t('Deleting...') : t('Delete Line')}
                    </Button>
                ) : (
                    <div /> /* spacer keeps Close on the right */
                )}

                <div className="d-flex gap-2">
                    {/* Save — hidden in read-only mode */}
                    {!ro && (
                        <Button
                            variant="primary"
                            onClick={handleSave}
                            disabled={busy || !editing}
                        >
                            {isSaving ? t('Saving...') : t('Save')}
                        </Button>
                    )}
                    <Button variant="secondary" onClick={handleClose} disabled={busy}>
                        {t('Close')}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
